// newUuid — a client-minted RFC-4122 v4 id that works on every browser the
// fleet actually runs.
//
// WHY THIS EXISTS (2026-09-11, Sentry KF49-PACKHOUSE-C0 / -BT). A bare
// `crypto.randomUUID()` is Chrome 92+ / Safari 15.4+. The Blackview BV4900
// rugged handsets on the floor ship Chrome Mobile 86 (Android 10) and never
// update, and there is no polyfill anywhere in the bundle (index.html carries
// only the prototype-freeze shim; no @vitejs/plugin-legacy). uiTelemetry.ts
// called it from the route-change effect in App.tsx — so on those devices the
// SHELL threw at /login, before a single screen rendered, and the ErrorBoundary
// was the whole app.
//
// Three tiers, best first, and the fallback is the SAME shape (a lowercase
// 8-4-4-4-12 v4 string), so nothing downstream — idempotency keys, DB uuid
// columns, storage paths — can tell which tier minted it:
//   1. crypto.randomUUID()             — native, when present.
//   2. crypto.getRandomValues + format — Chrome 11+/Safari 5+; CSPRNG, so the
//                                        collision odds are the native ones.
//   3. Math.random                     — last resort for a runtime with no
//                                        WebCrypto at all. NOT a CSPRNG; the
//                                        ids are unique enough for client
//                                        idempotency keys, which is all we mint.
//
// ⚠️ This is the ONLY file allowed to say `crypto.randomUUID` —
// scripts/check-bare-random-uuid.mjs fails the build on any other call site.

const HEX: string[] = Array.from({ length: 256 }, (_, i) => (i + 0x100).toString(16).slice(1))

function fromBytes(b: Uint8Array): string {
  // RFC 4122 §4.4: version nibble 0100 in byte 6, variant 10xx in byte 8.
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  return (
    HEX[b[0]] + HEX[b[1]] + HEX[b[2]] + HEX[b[3]] + '-' +
    HEX[b[4]] + HEX[b[5]] + '-' +
    HEX[b[6]] + HEX[b[7]] + '-' +
    HEX[b[8]] + HEX[b[9]] + '-' +
    HEX[b[10]] + HEX[b[11]] + HEX[b[12]] + HEX[b[13]] + HEX[b[14]] + HEX[b[15]]
  )
}

/** A fresh v4 UUID, on any browser. See the header for the three tiers. */
export function newUuid(): string {
  const c = globalThis.crypto
  if (c && typeof c.randomUUID === 'function') return c.randomUUID()
  const bytes = new Uint8Array(16)
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes)
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256)
  }
  return fromBytes(bytes)
}
